import { z } from 'zod';

// ==================== REQUEST SCHEMAS ====================

export const AddToLibrarySchema = z.object({
  modelId: z.string().min(1),
  note: z.string().optional(),
});

export const UpdateLibraryEntrySchema = z.object({
  note: z.string().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const ReorderLibrarySchema = z.object({
  entries: z.array(
    z.object({
      modelId: z.string().min(1),
      sortOrder: z.number().int().min(0),
    })
  ),
});

/** The catalog filter a bulk action applies to — mirrors GET /api/model-management. */
export const LibraryBulkFilterSchema = z.object({
  search: z.string().optional(),
  nodeType: z.enum(['image', 'video', 'sound', '3d', 'text-llm']).optional(),
  modelFamily: z.string().optional(),
  purpose: z.enum(['create', 'edit', 'enhance', 'tools']).optional(),
  purposeNot: z.enum(['create', 'edit', 'enhance', 'tools']).optional(),
  subPurpose: z.string().optional(),
});

/** Add or remove many models at once: explicit ids, or every enabled model matching a filter. */
export const BulkLibrarySchema = z.object({
  action: z.enum(['add', 'remove']),
  modelIds: z.array(z.string().min(1)).min(1).max(2000).optional(),
  filter: LibraryBulkFilterSchema.optional(),
}).refine((body) => Boolean(body.modelIds) !== Boolean(body.filter), {
  message: 'Provide either modelIds or filter',
});

export const BulkLibraryResultSchema = z.object({
  action: z.enum(['add', 'remove']),
  modelIds: z.array(z.string()),
});

export const LibraryQuerySchema = z.object({
  nodeType: z.string().optional(),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const RecentlyUsedQuerySchema = z.object({
  nodeType: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const RecordUsageSchema = z.object({
  modelId: z.string().min(1),
  nodeType: z.string().min(1),
});

// ==================== RESPONSE SCHEMAS ====================

export const LibraryModelInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  nodeType: z.string().nullable(),
  providerCategory: z.string(),
  modelFamilyId: z.string().nullable(),
  modelFamilySlug: z.string().nullable(),
  modelFamilyName: z.string().nullable(),
  purpose: z.string().nullable(),
  featuredScore: z.number(),
  enabled: z.boolean(),
  thumbnailUrl: z.string().nullable().optional(),
});

export const LibraryEntrySchema = z.object({
  id: z.string(),
  modelId: z.string(),
  note: z.string().nullable(),
  sortOrder: z.number(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const LibraryEntryWithModelSchema = LibraryEntrySchema.extend({
  model: LibraryModelInfoSchema,
});

export const RecentlyUsedEntrySchema = z.object({
  id: z.string(),
  modelId: z.string(),
  nodeType: z.string(),
  usageCount: z.number(),
  lastUsedAt: z.string().datetime(),
  model: LibraryModelInfoSchema,
});

// ==================== TYPES ====================

export type AddToLibrary = z.infer<typeof AddToLibrarySchema>;
export type UpdateLibraryEntry = z.infer<typeof UpdateLibraryEntrySchema>;
export type ReorderLibrary = z.infer<typeof ReorderLibrarySchema>;
export type LibraryBulkFilter = z.infer<typeof LibraryBulkFilterSchema>;
export type BulkLibrary = z.infer<typeof BulkLibrarySchema>;
export type BulkLibraryResult = z.infer<typeof BulkLibraryResultSchema>;
export type LibraryQuery = z.infer<typeof LibraryQuerySchema>;
export type RecentlyUsedQuery = z.infer<typeof RecentlyUsedQuerySchema>;
export type RecordUsage = z.infer<typeof RecordUsageSchema>;
export type LibraryEntry = z.infer<typeof LibraryEntrySchema>;
export type LibraryEntryWithModel = z.infer<typeof LibraryEntryWithModelSchema>;
export type RecentlyUsedEntry = z.infer<typeof RecentlyUsedEntrySchema>;
